import LinkButton from '@carvana/ds-link-button';
import Popup, { Option } from '@carvana/ds-popup';
import { icon } from '@carvana/ds-styles';
import { SearchSource } from '@carvana/merch-contracts';
import cx from 'classnames';
import { ChevronDown } from 'lucide-react';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import SortOption from '~/constants/common/SortOption';
import { SortOptionsWithLabels } from '~/constants/srp/SortOption';
import AppliedFiltersContext from '~/context/common/AppliedFiltersContext';
import { useIsMobile } from '~/context/common/DeviceContext';
import { useModalContext } from '~/context/srp/ModalContext/ModalContext';
import SearchEventsDataContext from '~/context/srp/SearchEventsDataContext';
import useGetAvailableSortOptions from '~/hooks/srp/useGetAvailableSortOptions';
import { useIsSortByInView } from '~/hooks/srp/useIsSortByInView';

export default function SortBy() {
  const { setSearchSource } = useContext(SearchEventsDataContext);
  const { setIsSortUserSelected, setSortBy, sortBy } = useContext(
    AppliedFiltersContext
  );
  const [isOpen, setOpen] = useState(false);
  const [ref, inView] = useIsSortByInView();
  const isMobile = useIsMobile();
  const { setSortByModalOpen } = useModalContext();
  const availableSortOptions = useGetAvailableSortOptions();
  const isPopupOpen = !isMobile && isOpen;

  const toggle = useCallback(() => {
    if (isMobile) {
      setSortByModalOpen(true);
    } else {
      setOpen(v => !v);
    }
  }, [isMobile, setSortByModalOpen]);

  const sortedBy = useMemo(
    () =>
      SortOptionsWithLabels.find(s => s.key === sortBy)?.label ?? 'Recommended',
    [sortBy]
  );

  useEffect(() => {
    if (!inView && isOpen) {
      setOpen(false);
    }
  }, [inView, isOpen]);

  const handleOnChange = (
    children: React.ReactNode,
    event: any,
    value?: any
  ) => {
    setIsSortUserSelected(true);
    setSortBy(value as SortOption);
    setSearchSource(SearchSource.SortBy);
    setOpen(false);
  };

  const control = (
    <div className="relative">
      <LinkButton
        onClick={toggle}
        data-adobe-id="sort-by"
        data-analytics-id="Sort Button"
        data-testid="sort-button"
        className="flex items-center gap-sm sm:gap-md"
        variant="default"
        size="sm"
        underline={false}
      >
        <p
          className="text-text-primary t-label-sm sm:text-text-default"
          ref={ref}
        >
          Sort{' '}
          <span className="hidden sm:inline">
            by{' '}
            <span
              className={cx(
                isOpen ? 'text-text-primary-active' : 'text-text-primary'
              )}
            >
              {sortedBy}
            </span>
          </span>
        </p>
        <ChevronDown
          size={16}
          color={isOpen ? icon.primaryActive : icon.primary}
          className={isOpen ? 'rotate-180' : ''}
        />
      </LinkButton>
    </div>
  );

  return (
    <>
      <div
        className={
          isPopupOpen
            ? 'fixed inset-0 z-[10] h-screen w-full bg-canvas-overlay'
            : 'hidden'
        }
      />
      <Popup
        key={sortBy}
        align="center"
        variant="list"
        directions={['bottom']}
        initialValue={sortBy}
        onChange={handleOnChange}
        onClickOutside={() => setOpen(false)}
        open={isPopupOpen}
        control={control}
      >
        {availableSortOptions.map(sortOption => {
          return (
            <Option key={sortOption.label} value={sortOption.key}>
              <span className="t-body-md">{sortOption.label}</span>
            </Option>
          );
        })}
      </Popup>
    </>
  );
}
